import {Amplify} from 'aws-amplify'
import * as AmplifyAuth from 'aws-amplify/auth'

import {baseApi} from './rest'

export enum SignInStepOptions {
  CONFIRM_SIGN_IN_WITH_SMS_CODE = 'CONFIRM_SIGN_IN_WITH_SMS_CODE',
  CONTINUE_SIGN_IN_WITH_MFA_SELECTION = 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION',
  CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED = 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
}

/**
 * authentication effects
 */

const UserPoolId = process.env.REACT_APP_COGNITO_USERPOOL
const ClientId = process.env.REACT_APP_COGNITO_CLIENTID || ''
const CognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN || ''
const RedirectSignIn = process.env.REACT_APP_SSO_REDIRECT_URI || 'http://localhost:3000/login/sso'
const RedirectSignOut = process.env.REACT_APP_SSO_SIGNOUT_URI || 'http://localhost:3000/login'
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: UserPoolId!,
      userPoolClientId: ClientId!,
      loginWith: {
        oauth: {
          domain: CognitoDomain,
          scopes: ['email', 'openid', 'phone'],
          redirectSignIn: [RedirectSignIn],
          redirectSignOut: [RedirectSignOut],
          responseType: 'code'
        },
        email: true
      }
    }
  }
})

const login = async (email: string, password: string) => {
  try {
    const {isSignedIn, nextStep} = await AmplifyAuth.signIn({
      username: email,
      password: password
    })
    return {isSignedIn, nextStep}
  } catch (err) {
    if ((err as Error).name === 'UserAlreadyAuthenticatedException') {
      console.log('User is already authenticated, attempting to sign out and get current user again')
      await AmplifyAuth.signOut()
    }
    throw err
  }
}

const redirectToMicrosoftSso = async (provider: string) => {
  await AmplifyAuth.signInWithRedirect({provider: {custom: provider}, options: {prompt: 'LOGIN'}})
}

const confirmSignIn = async (challengeResponse: string, userAttributes?: any) => {
  try {
    const confirmSignInObject = userAttributes
      ? {
          challengeResponse: challengeResponse,
          options: {
            userAttributes: {
              email: userAttributes.email,
              password: userAttributes.password
            }
          }
        }
      : {challengeResponse: challengeResponse}
    return AmplifyAuth.confirmSignIn(confirmSignInObject) as Promise<AmplifyAuth.ConfirmSignInOutput>
  } catch (err) {
    throw err
  }
}

const getTokens = async (refresh?: boolean) => {
  try {
    const {accessToken, idToken} =
      (await AmplifyAuth.fetchAuthSession({forceRefresh: refresh ?? undefined})).tokens ?? {}
    return {accessToken, idToken}
  } catch (err) {
    console.log(err)
  }
}
const getCurrentUser = async () => {
  try {
    const {username, userId, signInDetails} = await AmplifyAuth.getCurrentUser()

    return {username, userId, signInDetails}
  } catch (err) {
    console.log(err)
  }
}

const logout = async () => {
  await AmplifyAuth.signOut()
  console.log('User signed out, redirecting to Cognito logout endpoint to clear SSO session')
  const cognitoLogoutUrl = `https://${CognitoDomain}/logout?client_id=${ClientId}&logout_uri=${RedirectSignOut}`
  window.location.href = cognitoLogoutUrl
}

const completeForceResetPassword = async (
  newPassword: string,
  email: string,
  confirmationCode: string
): Promise<void> => {
  await AmplifyAuth.confirmResetPassword({
    username: email,
    newPassword: newPassword,
    confirmationCode: confirmationCode
  })
}

const forgotPassword = async (username: string) => {
  return await AmplifyAuth.resetPassword({username})
}

const confirmResetPassword = async (username: string, newPassword: string, confirmationCode: string) => {
  await AmplifyAuth.confirmResetPassword({username, newPassword, confirmationCode})
}

const updatePassword = async (oldPassword: string, newPassword: string) => {
  return await AmplifyAuth.updatePassword({oldPassword, newPassword})
}

const authApi = {
  login,
  getTokens,
  confirmSignIn,
  logout,
  forgotPassword,
  getCurrentUser,
  confirmResetPassword,
  updatePassword,
  completeForceResetPassword,
  redirectToMicrosoftSso,
  ...baseApi,
  async resolveAuthenticationMethod(email: string) {
    const req = await this.doRequest({
      path: '/public/auth/resolve',
      payload: {data: {email}},
      isPublic: true
    })
    const res = await req.post()
    return res.data
  }
}

export default authApi
